import { migratePLAExpression, parsePLAExpression } from "./PLAExpression";
import { getSortedPLAAttributeEntries } from "./PLAAttributeDefinitions";
import type { PLAElementRecord, PLAProject } from "./PLAState";

type ExpressionNode = ReturnType<typeof parsePLAExpression>;

export type PLAFormulaSource = {
   elementTypeId: string;
   attributeId: string;
   elementId?: string;
   elementName?: string;
   alertId?: string;
   expression: string;
};

export type PLAAttributeUsage = {
   elementTypeId: string;
   attributeId: string;
   source: PLAFormulaSource;
   crossElement: boolean;
};

export type PLAAttributeUsageIssue = {
   source: PLAFormulaSource;
   reason: "dynamic" | "unknown" | "parse";
   detail: string;
};

function staticName(node: ExpressionNode | undefined): string | undefined {
   if (node?.type === "string") return node.value;
   if (node?.type === "function" && node.name === "concat") {
      const parts = node.args.map(staticName);
      if (parts.every((part) => part !== undefined)) return parts.join("");
   }
   return undefined;
}

export function getPLAAttributeUsage(project: PLAProject, elements: PLAElementRecord[]) {
   const sources: PLAFormulaSource[] = [];
   const usage: PLAAttributeUsage[] = [];
   const issues: PLAAttributeUsageIssue[] = [];
   const elementTypes = Object.entries(project.elementTypes).sort(([, a], [, b]) => a.sortOrder - b.sortOrder);

   for (const [elementTypeId, elementType] of elementTypes) {
      for (const [attributeId, attribute] of getSortedPLAAttributeEntries(elementType.attributes)) {
         if (attribute.type === "expression" && attribute.expression?.trim()) {
            sources.push({ elementTypeId, attributeId, expression: attribute.expression });
         }
         for (const [alertId, alert] of Object.entries(attribute.alerts || {})) {
            if (alert.booleanExpression.trim()) sources.push({ elementTypeId, attributeId, alertId, expression: alert.booleanExpression });
         }
      }
   }
   for (const element of elements) {
      for (const [attributeId, attribute] of Object.entries(element.attributes)) {
         if (attribute.mode === "expression" && attribute.expression.trim()) {
            sources.push({ elementTypeId: element.elementType, attributeId, elementId: element.id, elementName: element.name, expression: attribute.expression });
         }
      }
   }

   for (const source of sources) {
      const seen = new Set<string>();
      const addReference = (name: string, localTypeId: string | undefined) => {
         if (name.trim().toLowerCase() === "name") return;
         let found = false;
         for (const [elementTypeId, elementType] of elementTypes) {
            if (localTypeId !== undefined && elementTypeId !== localTypeId) continue;
            const attributeId = Object.entries(elementType.attributes).find(([id, attribute]) => id === name || attribute.name === name)?.[0];
            if (attributeId === undefined) continue;
            found = true;
            const key = JSON.stringify([elementTypeId, attributeId, localTypeId === undefined]);
            if (seen.has(key)) continue;
            seen.add(key);
            usage.push({ elementTypeId, attributeId, source, crossElement: localTypeId === undefined });
         }
         if (!found) issues.push({ source, reason: "unknown", detail: name });
      };

      // Pipe arguments normally run on the source element, except where predicates.
      const visit = (node: ExpressionNode, localTypeId: string | undefined): boolean => {
         switch (node.type) {
            case "self": return true;
            case "attribute":
               addReference(node.name, localTypeId);
               return false;
            case "binary":
               visit(node.left, localTypeId);
               visit(node.right, localTypeId);
               return false;
            case "conditional": {
               visit(node.condition, localTypeId);
               const left = visit(node.whenTrue, localTypeId);
               const right = visit(node.whenFalse, localTypeId);
               return left && right;
            }
            case "function":
               node.args.forEach((arg) => visit(arg, localTypeId));
               return false;
            case "pipe": {
               const localSelection = visit(node.input, localTypeId);
               const targetTypeId = localSelection ? localTypeId : undefined;
               const attributeArgumentIndexes = node.name === "toCube"
                  ? node.args.map((_arg, index) => index).filter((index) => index === node.args.length - 1 || index % 2 === 1)
                  : ["get", "getRaw", "whereAttribute"].includes(node.name) ? [0]
                     : ["downUntil", "upUntil", "relatedValues"].includes(node.name) ? [1] : [];
               node.args.forEach((arg, index) => {
                  if (node.name === "toCube" && arg.type === "function" && arg.name === "literal") return;
                  if (attributeArgumentIndexes.includes(index)) {
                     const name = staticName(arg);
                     if (name !== undefined) {
                        const traversed = ["downUntil", "upUntil", "relatedValues"].includes(node.name);
                        addReference(arg.type === "string" ? name : name.trim(), traversed ? undefined : targetTypeId);
                     } else {
                        issues.push({ source, reason: "dynamic", detail: node.name });
                     }
                  }
                  visit(arg, node.name === "where" ? targetTypeId : localTypeId);
               });
               if (["down", "up", "descendants", "ancestors", "downUntil", "upUntil", "upWith", "relatedValues"].includes(node.name)) return false;
               return localSelection;
            }
            default: return false;
         }
      };

      let node: ExpressionNode;
      try {
         node = parsePLAExpression(migratePLAExpression(source.expression));
      } catch (error) {
         issues.push({ source, reason: "parse", detail: error instanceof Error ? error.message : String(error) });
         continue;
      }
      visit(node, source.elementTypeId);
   }
   return { usage, issues };
}
